import { useQueryClient } from '@tanstack/react-query';
import { MarketClient } from 'clients';
import { QueryKeys } from 'enums';
import { Market, MarketCreate } from 'types';
import { UseQueryBaseRetrieveOptions, UseQueryBaseSearchOptions } from './query/types';
import { useBulkCreate } from './query/useBulkCreate';
import { useMutate } from './query/useMutate';
import { useQueryRetrieve, useQuerySearch } from './query/useQuery';

export function useSearchMarket(props?: UseQueryBaseSearchOptions<Market>) {
  const { filter, queryOptions, enabled } = props || {};
  return useQuerySearch({
    queryKey: QueryKeys.Markets,
    clientClass: MarketClient,
    enabled,
    filter,
    queryOptions: {
      ...queryOptions,
    },
  });
}

export function useRetrieveMarket(props: UseQueryBaseRetrieveOptions<Market>) {
  const { id, queryOptions, enabled } = props;
  return useQueryRetrieve({
    queryKey: QueryKeys.Markets,
    clientClass: MarketClient,
    id,
    enabled,
    queryOptions: {
      ...queryOptions,
    },
  });
}

export function useMutateMarket() {
  const queryClient = useQueryClient();

  const { create: createMarket, update: updateMarket } = useMutate<Market, Market, MarketCreate, MarketClient>({
    queryKey: QueryKeys.Markets,
    clientClass: MarketClient,
    updateOptions: { throwOnError: true },
  });

  const { create: createMarketByName } = useMutate<Market, Market, MarketCreate, MarketClient>({
    queryKey: QueryKeys.Markets,
    clientClass: MarketClient,
    createOptions: {
      subPath: 'by-name',
      throwOnError: true,
    },
  });

  const invalidateMarkets = () => queryClient.invalidateQueries([QueryKeys.Markets]);

  return { createMarket, updateMarket, createMarketByName, invalidateMarkets };
}

export function useBulkCreateMarkets() {
  const { bulkCreate: bulkCreateMarkets } = useBulkCreate<Market, Market, MarketClient>({
    queryKey: QueryKeys.Markets,
    clientClass: MarketClient,
    createOptions: { throwOnError: true },
  });

  return { bulkCreateMarkets };
}
